/** Fallback when a rate-limit refusal carries no usable wait. */
export const DEFAULT_RATE_LIMIT_RETRY_SECONDS = 60;

/** Extra wait spread across clients that hit the limit together. */
export const COOLDOWN_JITTER_MAX_MS = 3_000;

/**
 * Seconds until the client may send again. Accepts the in-band
 * `retry_after_seconds` number or a `Retry-After` delta-seconds / HTTP-date.
 * Missing, zero, and negative values fall through so the caller can apply the
 * default wait.
 */
export const parseRetryAfterSeconds = (value: unknown, now = Date.now()): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.ceil(value);
  }

  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return seconds > 0 ? Math.ceil(seconds) : undefined;
  }

  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;

  const seconds = Math.ceil((dateMs - now) / 1000);
  return seconds > 0 ? seconds : undefined;
};

export const computeCooldownUntil = (
  retryAfterSeconds: number | undefined,
  now = Date.now(),
  jitterMs = Math.floor(Math.random() * (COOLDOWN_JITTER_MAX_MS + 1))
): number => {
  const seconds =
    retryAfterSeconds !== undefined && retryAfterSeconds > 0
      ? retryAfterSeconds
      : DEFAULT_RATE_LIMIT_RETRY_SECONDS;
  const jitter = Math.min(COOLDOWN_JITTER_MAX_MS, Math.max(0, Math.floor(jitterMs)));
  return now + seconds * 1000 + jitter;
};

export const isCooldownActive = (until: unknown, now = Date.now()): boolean =>
  typeof until === 'number' && Number.isFinite(until) && until > now;

/** Live countdown clock, `m:ss` (for example `4:10`). */
export const formatCooldownClock = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};
